import { z } from 'zod';

// UserRole schema
export const userRoleSchema = z.enum(['admin', 'user', 'viewer']);
export type UserRole = z.infer<typeof userRoleSchema>;

// Permission schema
export const permissionSchema = z.enum([
  'read',
  'write',
  'delete',
  'admin',
  'manage_users',
  'manage_models',
  'manage_knowledge',
  'manage_literature',
]);
export type Permission = z.infer<typeof permissionSchema>;

// User schema
export const userSchema = z.object({
  id: z.number(),
  email: z.email(),
  full_name: z.string(),
  role: userRoleSchema,
  avatar_url: z.url().nullable(),
  created_at: z.string(),
  last_login: z.string().nullable(),
  is_active: z.boolean(),
  is_verified: z.boolean(),
  bio: z.string().nullable(),
  permissions: z.array(z.string()),
});
export type User = z.infer<typeof userSchema>;

// Login credentials schema
export const loginCredentialsSchema = z.object({
  email: z.email().min(1, 'auth.errors.emailRequired'),
  password: z
    .string()
    .min(1, 'auth.errors.passwordRequired')
    .min(6, 'auth.errors.passwordTooShort')
    .max(100, 'auth.errors.passwordTooLong'),
});
export type LoginCredentials = z.infer<typeof loginCredentialsSchema>;

// Register data schema
export const registerDataSchema = z
  .object({
    email: z.email().min(1, 'auth.errors.emailRequired'),
    username: z
      .string()
      .min(1, 'auth.errors.usernameRequired')
      .min(2, 'auth.errors.usernameTooShort')
      .max(50, 'auth.errors.usernameTooLong'),
    password: z
      .string()
      .min(1, 'auth.errors.passwordRequired')
      .min(6, 'auth.errors.passwordTooShort')
      .max(100, 'auth.errors.passwordTooLong'),
    confirm_password: z.string().min(1, 'auth.errors.confirmPasswordRequired'),
  })
  .refine(data => data.password === data.confirm_password, {
    message: 'auth.errors.passwordMismatch',
    path: ['confirm_password'],
  });
export type RegisterData = z.infer<typeof registerDataSchema>;

// Password change data schema
export const passwordChangeDataSchema = z
  .object({
    current_password: z.string().min(1, 'auth.errors.currentPasswordRequired'),
    new_password: z
      .string()
      .min(1, 'auth.errors.newPasswordRequired')
      .min(6, 'auth.errors.passwordTooShort')
      .max(100, 'auth.errors.passwordTooLong'),
  })
  .refine(data => data.current_password !== data.new_password, {
    message: 'auth.errors.newPasswordSameAsOld',
    path: ['new_password'],
  });
export type PasswordChangeData = z.infer<typeof passwordChangeDataSchema>;

// Auth state schema
export const authStateSchema = z.object({
  user: userSchema.nullable(),
  isAuthenticated: z.boolean(),
  isLoading: z.boolean(),
  error: z.string().nullable(),
  permissions: z.array(permissionSchema),
});
export type AuthState = z.infer<typeof authStateSchema>;

// Auth response schema
export const authResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

// Login response schema
export const loginResponseSchema = z.object({
  user: userSchema,
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// Register response schema
export const registerResponseSchema = z.object({
  user: z.object({
    id: z.number(),
    email: z.string().email(),
    username: z.string(),
  }),
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// Auth action schema (discriminated union)
export const authActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('LOGIN_START') }),
  z.object({
    type: z.literal('LOGIN_SUCCESS'),
    payload: z.object({
      user: userSchema,
      permissions: z.array(permissionSchema),
    }),
  }),
  z.object({ type: z.literal('LOGIN_FAILURE'), payload: z.string() }),
  z.object({ type: z.literal('LOGOUT') }),
  z.object({ type: z.literal('REGISTER_START') }),
  z.object({
    type: z.literal('REGISTER_SUCCESS'),
    payload: z.object({
      user: userSchema,
      permissions: z.array(permissionSchema),
    }),
  }),
  z.object({ type: z.literal('REGISTER_FAILURE'), payload: z.string() }),
  z.object({ type: z.literal('LOAD_USER_START') }),
  z.object({
    type: z.literal('LOAD_USER_SUCCESS'),
    payload: z.object({
      user: userSchema,
      permissions: z.array(permissionSchema),
    }),
  }),
  z.object({ type: z.literal('LOAD_USER_FAILURE'), payload: z.string() }),
  z.object({ type: z.literal('UPDATE_USER'), payload: userSchema.partial() }),
  z.object({ type: z.literal('CLEAR_ERROR') }),
]);
export type AuthAction = z.infer<typeof authActionSchema>;

// Auth context type (not a Zod schema, just a TypeScript type)
export interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (userData: RegisterData) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}
